/**
 * Reception's Book and the Appointments screen's online rows: the form's
 * own checks, said before the server is asked. The server decides; these only spare a round trip.
 */
import { normaliseCode } from './appointmentCode'
import { otherMode } from './bookingFlow'
import type { StaffKey } from './staffText'
import type { Appointment, BookingChannel, BookingFormField, BookingFormFields, BookingSlot, FinMode } from './types'

export interface DeskForm {
  serviceId: string
  day: string
  /** The chosen slot's start, as the server listed it; '' until one is chosen. */
  start: string
  name: string
  fin: string
  channel: BookingChannel
  contact: string
  /** The contact the code does not go to, when the form asks it. */
  other: string
  /** The organisation's own fields by key. */
  extra: Record<string, string>
  language: 'az' | 'ru' | 'en'
}

/** The customer's messages start in the organisation's default customer language. */
export function emptyDeskForm(day: string, serviceId = '', language: DeskForm['language'] = 'en'): DeskForm {
  return { serviceId, day, start: '', name: '', fin: '', channel: 'sms', contact: '', other: '', extra: {}, language }
}

const PHONE = /^\+[1-9]\d{7,14}$/
const ADDRESS = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]+$/

/** The rules a desk form is checked by; a mode not given reads as before the
 *  booking form existed (name required, FIN optional, no other contact). */
export type DeskRules = Partial<BookingFormFields>

/** One problem the form says before Book; field names the other contact
 *  (mobile, email) or an own field's key, for {field}. */
export interface DeskProblem {
  key: StaffKey
  field?: string
}

const phoneOf = (v: string) => v.replace(/[\s().-]/g, '').replace(/^00/, '+')

/** One own field's value against its type; never stricter than catalog.Field.CheckValue. */
export function extraProblem(f: BookingFormField, raw: string): StaffKey | null {
  const v = raw.trim()
  if (!v) return f.mode === 'required' ? 'booking.problem.required' : null
  const bad =
    (f.type === 'number' && !Number.isFinite(Number(v))) ||
    (f.type === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(v)) ||
    (f.type === 'phone' && !PHONE.test(phoneOf(v))) ||
    (f.type === 'choice' && !(f.choices ?? []).includes(v)) ||
    (f.maxLen !== undefined && f.maxLen > 0 && [...v].length > f.maxLen)
  return bad ? 'booking.problem.shape' : null
}

export function deskProblem(f: DeskForm, rules: DeskRules = {}): DeskProblem | null {
  if (!f.start) return { key: 'booking.noSlots' }
  const nameMode = rules.name ?? 'required'
  const name = f.name.trim()
  if (nameMode !== 'off' && ((nameMode === 'required' && name === '') || name.length > 100)) return { key: 'booking.problem.name' }
  const finMode: FinMode = rules.fin ?? 'optional'
  const fin = f.fin.trim()
  if (finMode !== 'off' && ((finMode === 'required' && fin === '') || (fin && !/^[A-Za-z0-9]{7}$/.test(fin)))) {
    return { key: 'booking.problem.fin' }
  }
  const contact = f.channel === 'sms' ? phoneOf(f.contact) : f.contact.trim()
  if (!(f.channel === 'sms' ? PHONE : ADDRESS).test(contact)) return { key: 'booking.problem.contact' }
  const asked = otherMode({ mobile: rules.mobile ?? 'off', email: rules.email ?? 'off' }, f.channel)
  const other = f.other.trim()
  const field = f.channel === 'sms' ? 'email' : 'mobile'
  if (asked === 'required' && other === '') return { key: 'booking.problem.required', field }
  if (asked !== 'off' && other && !(f.channel === 'sms' ? ADDRESS.test(other) : PHONE.test(phoneOf(other)))) {
    return { key: 'booking.problem.shape', field }
  }
  for (const x of rules.extra ?? []) {
    const key = extraProblem(x, f.extra[x.key] ?? '')
    if (key) return { key, field: x.key }
  }
  return null
}

/** What the Book form draws: no FIN and no contact until the organisation's
 *  needs are known (null), then the FIN unless off, and a channel it can send on. */
export function deskFields(finMode: FinMode | null, channels: readonly BookingChannel[] | null, chosen: BookingChannel) {
  return {
    fin: finMode !== null && finMode !== 'off',
    contact: channels === null || channels.length === 0 ? null : channels.includes(chosen) ? chosen : channels[0]!,
    choices: channels && channels.length > 1 ? channels : [],
    // Nothing to send the code on: the server refuses every booking, so say it before typing.
    noChannel: channels !== null && channels.length === 0,
  }
}

/** The booking request: the name only when asked, the other contact and own fields only when given. */
export function deskBody(f: DeskForm, rules: DeskRules = {}) {
  const other = f.other.trim()
  const extra: Record<string, string> = {}
  for (const x of rules.extra ?? []) {
    const v = (f.extra[x.key] ?? '').trim()
    if (v) extra[x.key] = v
  }
  const asked = otherMode({ mobile: rules.mobile ?? 'off', email: rules.email ?? 'off' }, f.channel)
  return {
    service: f.serviceId,
    start: f.start,
    name: rules.name === 'off' ? '' : f.name.trim(),
    fin: f.fin.trim().toUpperCase(),
    channel: f.channel,
    contact: f.contact.trim(),
    language: f.language,
    ...(asked !== 'off' && other ? { [f.channel === 'sms' ? 'email' : 'mobile']: other } : {}),
    ...(Object.keys(extra).length > 0 ? { fields: extra } : {}),
  }
}

export function freeSlots(slots: readonly BookingSlot[]): BookingSlot[] {
  return slots.filter((s) => s.free > 0)
}

export function sourceKey(a: Appointment): StaffKey | null {
  if (a.source === 'online') return 'booking.source.online'
  if (a.source === 'reception') return 'booking.source.reception'
  return null
}

/** Moved on the slot grid only an online or reception booking still booked. */
export function canChange(a: Appointment): boolean {
  return a.state === 'booked' && (a.source === 'online' || a.source === 'reception')
}

/** What the details drawer offers: an online or reception booking is cancelled
 *  without the branch's seat (a lapse honours it); an import's cancel and any move need it. */
export function deskActions(a: Appointment, seated: boolean): { cancel: boolean; move: boolean } {
  const ours = a.source === 'online' || a.source === 'reception'
  return { cancel: a.state === 'booked' && (seated || ours), move: seated && canChange(a) }
}

/** Whether the details drawer may show. Move takes its place rather than
 *  stacking beside it, so only one drawer is ever mounted at once. */
export function showsDetails(opened: Appointment | undefined, moving: Appointment | null): boolean {
  return Boolean(opened) && !moving
}

const ours = (a: Appointment) => a.source === 'online' || a.source === 'reception'

/** What a row says under the name. An online or reception booking's reference
 *  is a system's B-/R-<uuid>, so only its code is shown. */
export function codeLine(a: Appointment): { reference?: string; code?: string } {
  if (ours(a)) return { code: a.code }
  if (a.code === normaliseCode(a.reference)) return { reference: a.reference }
  return { reference: a.reference, code: a.code }
}

/** codeLine under a first line that may already be the reference: not said twice. */
export function codeLineUnder(a: Appointment, first: string): { reference?: string; code?: string } {
  const line = codeLine(a)
  return line.reference === first ? { code: line.code } : line
}

/** How staff name a booking aloud: its code when it is ours, else its reference. */
export function bookingLabel(a: Appointment): string {
  return ours(a) ? a.code : a.reference
}

/** Who a booking is for, in a line about it: the customer's name, else bookingLabel. */
export function bookingWho(a: Appointment): string {
  return a.customerName?.trim() || bookingLabel(a)
}

export type DeskPlace = 'slot' | 'name' | 'fin' | 'contact' | 'other' | `extra:${string}` | 'foot'

/** Where a field the server names sits: the contact the code goes to, the other one, or an own field. */
export function fieldPlace(field: string, channel: BookingChannel): DeskPlace {
  if (field === 'name') return 'name'
  if (field === 'mobile') return channel === 'sms' ? 'contact' : 'other'
  if (field === 'email') return channel === 'email' ? 'contact' : 'other'
  return `extra:${field}`
}

/** Where a refusal is said in the drawer: beside what caused it. */
export function refusalPlace(code: string | undefined, reason?: string, field?: string, channel: BookingChannel = 'sms'): DeskPlace {
  switch (code) {
    case 'SLOT_FULL':
    case 'SLOT_NOT_OFFERED':
    case 'TOO_LATE_TO_CHANGE':
    case 'BOOKING_NOT_OFFERED':
    case 'DAY_FULL':
      return 'slot'
    case 'FIN_REQUIRED':
    case 'FIN_SHAPE':
      return 'fin'
    case 'CONTACT_SHAPE':
    case 'CHANNEL_UNAVAILABLE':
    case 'TOO_MANY_OPEN':
      return 'contact'
    case 'FIELD_REQUIRED':
    case 'FIELD_SHAPE':
      return field ? fieldPlace(field, channel) : 'foot'
    case 'VALIDATION_FAILED':
      return reason === 'name' ? 'name' : reason === 'start' ? 'slot' : reason === 'channel' ? 'contact' : 'foot'
  }
  return 'foot'
}

/** Whether this browser can copy: a plain-HTTP desk has no clipboard. */
export function canCopy(nav: { clipboard?: unknown } | undefined = globalThis.navigator): boolean {
  return !!nav?.clipboard
}

/** Booked online or at Reception: the bookings a manage link can reach. */
export function mayHaveLink(a: Appointment): boolean {
  return a.source === 'online' || a.source === 'reception'
}

/** The manage-link row: a Show link button until pressed, since each read is
 *  on the audit trail; then the link, or nothing for a booking with none. */
/** Reads the manage link; a failed read leaves Show link pressable again. */
export async function readLink(fetch: () => Promise<{ manageUrl: string }>): Promise<{ asked: boolean; link: string }> {
  try {
    const r = await fetch()
    return { asked: true, link: r.manageUrl }
  } catch {
    return { asked: false, link: '' }
  }
}

export function linkRow(a: Appointment, asked: boolean, link: string): 'button' | 'link' | null {
  if (!mayHaveLink(a)) return null
  if (!asked) return 'button'
  return link ? 'link' : null
}
